import { useState, useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { toast } from "sonner";
import { Send, Copy, Share2, CheckCircle, RotateCcw, CalendarIcon } from "lucide-react";

interface ReferralFormProps {
  userId: string;
  onReferralCreated?: () => void;
}

interface MemberOption {
  id: string;
  name: string;
  company: string;
  chapter: string;
  email: string | null;
  phone: string | null;
  additional_chapters?: string[];
}

type ChapterValue = "washington" | "union" | "all";

interface SubmittedReferral {
  referringMemberName: string;
  memberName: string;
  memberCompany: string;
  memberEmail: string;
  memberPhone: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  referralMessage: string;
}

function buildFormattedText(ref: SubmittedReferral): string {
  let text = `Hi ${ref.memberName},\n\nThis is ${ref.referringMemberName}. I'm referring ${ref.contactName} to you.\n\nDetails: ${ref.referralMessage}\n\nContact info:`;
  if (ref.contactEmail) text += `\nEmail: ${ref.contactEmail}`;
  if (ref.contactPhone) text += `\nPhone: ${ref.contactPhone}`;
  text += `\n\n— Sent via RCN`;
  return text;
}

const ReferralSummary = ({ data, onReset }: { data: SubmittedReferral; onReset: () => void }) => {
  const [copied, setCopied] = useState(false);
  const isMobile = useIsMobile();
  const formattedText = buildFormattedText(data);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(formattedText);
      setCopied(true);
      toast.success("Copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy");
    }
  };

  const handleShare = async () => {
    if (isMobile && navigator.share) {
      try {
        await navigator.share({ text: formattedText });
      } catch (err: any) {
        if (err.name !== "AbortError") toast.error("Share failed");
      }
    } else {
      const subject = encodeURIComponent(`${data.referringMemberName} referring ${data.contactName} via RCN`);
      const body = encodeURIComponent(formattedText);
      const to = data.memberEmail ? encodeURIComponent(data.memberEmail) : "";
      window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
    }
  };

  return (
    <Card className="border-primary/30">
      <CardHeader className="text-center pb-2">
        <CheckCircle className="h-10 w-10 text-primary mx-auto mb-2" />
        <CardTitle>Referral Submitted!</CardTitle>
        <CardDescription>Copy or share the details below to notify {data.memberName}.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <pre className="whitespace-pre-wrap text-sm bg-muted rounded-md p-4 font-sans">{formattedText}</pre>
        <div className="flex gap-2">
          <Button onClick={handleCopy} variant="outline" className="flex-1">
            {copied ? <CheckCircle className="h-4 w-4 mr-2" /> : <Copy className="h-4 w-4 mr-2" />}
            {copied ? "Copied!" : "Copy to Clipboard"}
          </Button>
          <Button onClick={handleShare} className="flex-1">
            <Share2 className="h-4 w-4 mr-2" />
            Share
          </Button>
        </div>
        <Button variant="ghost" onClick={onReset} className="w-full">
          <RotateCcw className="h-4 w-4 mr-2" />
          Submit Another Referral
        </Button>
      </CardContent>
    </Card>
  );
};

const ReferralForm = ({ userId, onReferralCreated }: ReferralFormProps) => {
  const [loading, setLoading] = useState(false);
  const [members, setMembers] = useState<MemberOption[]>([]);
  const [myMember, setMyMember] = useState<MemberOption | null>(null);
  const [myChapters, setMyChapters] = useState<ChapterValue[]>([]);
  const [selectedChapter, setSelectedChapter] = useState<ChapterValue | "">("");
  const [submittedReferral, setSubmittedReferral] = useState<SubmittedReferral | null>(null);
  const [formData, setFormData] = useState<{
    referred_to_id: string;
    referral_message: string;
    contact_name: string;
    contact_email: string;
    contact_phone: string;
    referral_date: Date;
  }>({
    referred_to_id: "",
    referral_message: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    referral_date: new Date(),
  });

  const [loadError, setLoadError] = useState<string | null>(null);
  const [loadingMembers, setLoadingMembers] = useState(true);

  const fetchMyMember = async () => {
    if (!userId) {
      setLoadingMembers(false);
      return;
    }
    setLoadingMembers(true);
    setLoadError(null);
    try {
      const { data: myRecord, error: meErr } = await supabase
        .from("members")
        .select("id, name, company, chapter, email, phone, additional_chapters")
        .eq("user_id", userId)
        .maybeSingle();
      if (meErr) throw meErr;

      if (myRecord) {
        setMyMember(myRecord);
        const primary = myRecord.chapter as ChapterValue;
        const additional = ((myRecord.additional_chapters as string[]) || []).filter(
          (c) => c !== primary,
        ) as ChapterValue[];
        const chapters = [primary, ...additional];
        setMyChapters(chapters);
        setSelectedChapter(primary);
      }
    } catch (err: any) {
      setLoadError(err?.message || "Couldn't load referral form.");
    } finally {
      setLoadingMembers(false);
    }
  };

  useEffect(() => {
    fetchMyMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Refilter member list whenever the selected chapter changes
  useEffect(() => {
    const loadMembers = async () => {
      if (!myMember || !selectedChapter) return;
      const { data, error } = await supabase
        .from("members")
        .select("id, name, company, chapter, email, phone, additional_chapters")
        .or(`chapter.eq.${selectedChapter},additional_chapters.cs.{${selectedChapter}}`)
        .eq("is_hidden", false)
        .neq("id", myMember.id)
        .order("name");
      if (error) {
        setLoadError(error.message);
        return;
      }
      setMembers(data || []);
      // Reset selection if the previously chosen member isn't in this chapter
      setFormData((prev) =>
        prev.referred_to_id && !(data || []).some((m) => m.id === prev.referred_to_id)
          ? { ...prev, referred_to_id: "" }
          : prev,
      );
    };
    loadMembers();
  }, [myMember, selectedChapter]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!myMember) {
      toast.error("You need a member profile to submit referrals");
      return;
    }
    if (!formData.referred_to_id || !formData.referral_message.trim() || !formData.contact_name.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }
    if (!formData.contact_email?.trim() && !formData.contact_phone?.trim()) {
      toast.error("Please provide at least an email or phone number for the contact");
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.from("referrals").insert({
        referred_by_id: myMember.id,
        referred_to_id: formData.referred_to_id,
        referral_message: formData.referral_message.trim(),
        contact_name: formData.contact_name.trim(),
        contact_email: formData.contact_email?.trim() || null,
        contact_phone: formData.contact_phone?.trim() || null,
        chapter: (selectedChapter || myMember.chapter) as "washington" | "union" | "all",
        referral_date: format(formData.referral_date, "yyyy-MM-dd"),
      });

      if (error) throw error;

      const recipient = members.find((m) => m.id === formData.referred_to_id);
      setSubmittedReferral({
        referringMemberName: myMember.name,
        memberName: recipient?.name ?? "Member",
        memberCompany: recipient?.company ?? "",
        memberEmail: recipient?.email ?? "",
        memberPhone: recipient?.phone ?? "",
        contactName: formData.contact_name.trim(),
        contactEmail: formData.contact_email?.trim() ?? "",
        contactPhone: formData.contact_phone?.trim() ?? "",
        referralMessage: formData.referral_message.trim(),
      });

      onReferralCreated?.();
    } catch (error: any) {
      toast.error("Couldn't submit referral — check your connection and try again. Your entries are still here.");
      console.error("Referral submit failed:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setSubmittedReferral(null);
    setFormData({ referred_to_id: "", referral_message: "", contact_name: "", contact_email: "", contact_phone: "", referral_date: new Date() });
  };

  if (loadingMembers) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">Loading referral form...</CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="py-8 text-center space-y-3">
          <p className="text-sm text-muted-foreground">Couldn't load the referral form.</p>
          <p className="text-xs text-muted-foreground">{loadError}</p>
          <Button variant="outline" size="sm" onClick={fetchMyMember}>
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!myMember) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground">
          You need a member profile to submit referrals. Please complete your profile first.
        </CardContent>
      </Card>
    );
  }

  if (submittedReferral) {
    return <ReferralSummary data={submittedReferral} onReset={handleReset} />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Submit a Referral</CardTitle>
        <CardDescription>
          Pass business to a fellow chapter member by submitting a referral with the contact's details.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {myChapters.length > 1 && (
            <div className="space-y-2">
              <Label htmlFor="referral_chapter">Chapter *</Label>
              <Select value={selectedChapter} onValueChange={(val) => setSelectedChapter(val as ChapterValue)}>
                <SelectTrigger id="referral_chapter">
                  <SelectValue placeholder="Select chapter..." />
                </SelectTrigger>
                <SelectContent>
                  {myChapters.map((c) => (
                    <SelectItem key={c} value={c} className="capitalize">
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                You belong to multiple chapters — pick which chapter this referral is for.
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="referral_date">Referral Date *</Label>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  id="referral_date"
                  type="button"
                  variant="outline"
                  className={cn("w-full justify-start text-left font-normal", !formData.referral_date && "text-muted-foreground")}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {formData.referral_date ? format(formData.referral_date, "PPP") : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={formData.referral_date}
                  onSelect={(d) => d && setFormData({ ...formData, referral_date: d })}
                  disabled={(d) => d > new Date()}
                  initialFocus
                  className={cn("p-3 pointer-events-auto")}
                />
              </PopoverContent>
            </Popover>
            <p className="text-xs text-muted-foreground">Defaults to today. Backdate if logging older referrals.</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="referred_to">Refer To (Member) *</Label>
            <Select value={formData.referred_to_id} onValueChange={(val) => setFormData({ ...formData, referred_to_id: val })}>
              <SelectTrigger>
                <SelectValue placeholder="Select a member..." />
              </SelectTrigger>
              <SelectContent>
                {members.map((m) => (
                  <SelectItem key={m.id} value={m.id}>
                    {m.name} — {m.company}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="referral_message">Referral Details *</Label>
            <Textarea
              id="referral_message"
              placeholder="Describe what the contact needs, any relevant context..."
              value={formData.referral_message}
              onChange={(e) => setFormData({ ...formData, referral_message: e.target.value })}
              rows={4}
            />
          </div>

          <div className="border-t pt-4">
            <h4 className="text-sm font-medium mb-3">Contact Being Referred</h4>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="contact_name">Contact Name *</Label>
                <Input
                  id="contact_name"
                  placeholder="Full name"
                  value={formData.contact_name}
                  onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_email">Email</Label>
                <Input
                  id="contact_email"
                  type="email"
                  placeholder="email@example.com"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="contact_phone">Phone</Label>
                <Input
                  id="contact_phone"
                  type="tel"
                  placeholder="(555) 123-4567"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-2">At least one contact method (email or phone) is required.</p>
          </div>

          <Button type="submit" disabled={loading} className="w-full">
            <Send className="h-4 w-4 mr-2" />
            {loading ? "Submitting..." : "Submit Referral"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default ReferralForm;
